import React, { memo, useEffect } from 'react';
import { FormProvider } from 'react-hook-form';
import { useEffectOnce } from 'react-use';
import { ProductViewModel, ReservationArgs } from '~/lib/data-contract';
import { ButtonLegacy } from '~/shared/components';
import { useTracking, useTranslation } from '~/shared/utils';
import { Fields, useForm } from '~/shared/utils/form';
import { ReserveSectionForm, ReserveSectionProduct, ReserveSectionStores } from './';
import { useReservation } from './hooks/useReservation';
import { StyledPendingIndicator, StyledReserveContainer, StyledReserveSection } from './styled';

type ReserveContainerProps = {
    product: ProductViewModel;
    initialVariantId: string | null;
    setIsReserveActive: (isActive: boolean) => void;
};

const ReserveContainer = memo(
    ({ product, initialVariantId, setIsReserveActive }: ReserveContainerProps) => {
        const { translate } = useTranslation();
        const { trackPageView } = useTracking();
        const { isLoading, createReservation, isSuccess: isReceipt } = useReservation();

        const formMethods = useForm({
            validationSchema: 'reserve',
            mode: 'onTouched',
            defaultValues: {
                // Create a custom field for sizes
                variantId: initialVariantId,
                // It is not possible to select other quantities than 1 currently
                quantity: 1,
            },
        });

        useEffect(() => {
            if (initialVariantId) {
                formMethods.setValue('variantId', initialVariantId);
            }
        }, [initialVariantId, formMethods]);

        const onSubmit = (values: Fields) => {
            if (isLoading) {
                return;
            }
            createReservation(values as ReservationArgs);
        };

        const activeVariantId = formMethods.watch('variantId');

        useEffectOnce(() => {
            trackPageView();
        });

        return (
            <div>
                <FormProvider {...formMethods}>
                    <StyledPendingIndicator isFetching={isLoading}>
                        <StyledReserveContainer>
                            <StyledReserveSection>
                                <ReserveSectionProduct product={product} isReceipt={isReceipt} />
                            </StyledReserveSection>
                            {activeVariantId && (
                                <ReserveSectionStores product={product} isReceipt={isReceipt} />
                            )}
                            <StyledReserveSection hasBorder={!isReceipt}>
                                {isReceipt ? (
                                    <ButtonLegacy
                                        onClick={() => setIsReserveActive(false)}
                                        variant="secondary"
                                    >
                                        {translate('reservation.receipt.button')}
                                    </ButtonLegacy>
                                ) : (
                                    <ReserveSectionForm onSubmit={onSubmit} />
                                )}
                            </StyledReserveSection>
                        </StyledReserveContainer>
                    </StyledPendingIndicator>
                </FormProvider>
            </div>
        );
    }
);

export default ReserveContainer;
